import { Callout, css, styled } from 'canoe';
import { nanoid } from 'nanoid';
import React, { useRef, useState } from 'react';

import { VariableEditor } from 'craft/components/VariableEditor';
import { NO_ACTION } from 'craft/constants';
import { useEditorNode } from 'craft/hooks';
import { useViewportDom } from 'craft/state/viewport/selectors';
import { CustomNodeData } from 'craft/types';
import { mergeRefs } from 'craft/utils';

export const VariableWrapper = styled.span`
  ${({ theme }) => css`
    background-color: ${theme.colors.GlitterGrayD10};
    border-radius: ${theme.borderRadius.regular}
    cursor: pointer;
    padding: 0px ${theme.spaces.xs} ${theme.spaces.xxs};
    font-weight: bold;
    white-space: nowrap;
  `}
`;

export interface VariableAttributes {
  id: string;
  name: string;
  fallback: string;
}

interface VariableProps {
  attributes: any;
  children: any;
  element: {
    id: string;
    variable: VariableAttributes;
  };
  onUpdateFromCraft: () => void;
}

export const Variable = ({ element, attributes, children, onUpdateFromCraft }: VariableProps) => {
  const variableRef = useRef<HTMLSpanElement>(null);
  const [isVariableFocused, setIsVariableFocused] = useState(false);
  const { viewportWrapperEl } = useViewportDom();

  const { setCustom } = useEditorNode(element.id);
  const handleSave = (data: any) => {
    setCustom({
      variable: {
        id: nanoid(6),
        name: data.name,
        fallback: data.fallback,
      },
    } as CustomNodeData);
    onUpdateFromCraft();
    setIsVariableFocused(false);
  };

  return (
    <>
      {isVariableFocused && (
        <Callout
          referenceElement={variableRef.current}
          onClose={() => setIsVariableFocused(false)}
          closeKey="Escape"
          placement="bottom"
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
          ]}
        >
          {(calloutProps) => (
            <div
              data-craft-ignore-events
              data-focus-listener={NO_ACTION}
              ref={calloutProps.ref}
              style={calloutProps.style}
              {...calloutProps.attributes.popper}
            >
              <VariableEditor
                initialValue={element.variable}
                onClose={() => setIsVariableFocused(false)}
                onVariable={handleSave}
              />
            </div>
          )}
        </Callout>
      )}
      <VariableWrapper
        {...attributes}
        ref={mergeRefs([variableRef, attributes.ref])}
        data-focus-listener={NO_ACTION}
        onClick={() => window.requestAnimationFrame(() => setIsVariableFocused(!isVariableFocused))}
      >
        {'{ '}
        {children}
        {element.variable.name}
        {' }'}
      </VariableWrapper>
    </>
  );
};
